import { useState } from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import AddCircleOutlineOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import UnfoldLess from "@mui/icons-material/UnfoldLess";
import MoreVert from "@mui/icons-material/MoreVert";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import ProgramForm from "../ProgramForm";
import useActionMenu from "../../actprep-ui/hooks/ui/useActionMenu";
import AddWeekToProgramForm from "../AddWeekToProgramForm";
import { SimpleTreeView } from "@mui/x-tree-view-pro";
import { useGet } from "../../actprep-ui/hooks/api";
import { useEffect } from "react";
import ProgramWeekNavTree, {
  generateProgramWeekNavItems,
} from "../ProgramWeekNavTree/ProgramWeekNavTree";
import { useConfirm } from "material-ui-confirm";
import api from "../../actprep-ui/utils/api";

function generateProgramNavItems(program) {
  return generateProgramWeekNavItems(program.program_weeks);
}

const getAllItemIds = (navItems) => {
  const itemIds = [];
  const registerItemId = (item) => {
    if (item.children?.length) {
      itemIds.push(item.id);
      item.children.forEach(registerItemId);
    }
  };

  navItems.forEach(registerItemId);
  return itemIds;
};

export default function ProgramNavTree({ onProgramDaySelect }) {
  const confirm = useConfirm();
  const [action, setAction] = useState(null);
  const [allExpanded, setAllExpanded] = useState(false);
  const [programOption, setProgram] = useState(null);
  const [addWeekFormOpen, setAddWeekFormOpen] = useState(false);
  const [editProgramFormOpen, setEditProgramFormOpen] = useState(false);

  const onProgramWeekAdded = () => {
    setAction("reload");
    showSuccessSnackbar("Program Week Added!");
  };

  const handleDuplicateConfirmed = async (deepCopy = false) => {
    if (!program) {
      return;
    }
    const response = await api.post(
      apiUrls.admin.duplicateProgram(program.uuid),
      {
        deep: deepCopy,
      }
    );
    if (response.data?.success) {
      setProgram({
        uuid: response.data.program.uuid,
        label: response.data.program.__str__,
      });
    }
  };

  const handleDuplicateClick = (deepCopy = false) => {
    confirm({
      description: `Please confirm that you want to create a ${
        deepCopy ? "deep copy" : "copy"
      } of the '${program?.title}' Program.`,
    }).then(() => handleDuplicateConfirmed(deepCopy));
  };

  const { handleOpen: handleOpenActionMenu, ActionMenu } = useActionMenu({
    actions: [
      {
        label: "Edit",
        onClick: () => setEditProgramFormOpen(true),
      },
      {
        label: "Duplicate",
        onClick: () => handleDuplicateClick(false),
      },
      {
        label: "Full Duplicate",
        onClick: () => handleDuplicateClick(true),
      },
    ],
  });

  const { data: programData, refetch } = useGet(apiUrls.admin.programs(), {
    params: {
      search_params: {
        uuid: programOption?.uuid,
      },
      keep_related: true,
      include_curriculum_tree: true,
    },
    enabled: !!programOption?.uuid,
  });
  const [navItems, setNavItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const handleExpandedItemsChange = (event, expandedItemIds) => {
    setExpandedItems(expandedItemIds);
  };

  const program = programData?.data[0];

  useEffect(() => {
    if (program) {
      setNavItems(generateProgramNavItems(program));
    } else {
      setNavItems([]);
    }
  }, [programData, program]);

  useEffect(() => {
    if (!action) {
      return;
    }
    if (action === "expand-all") {
      setExpandedItems(getAllItemIds(navItems));
    } else if (action === "collapse-all") {
      setExpandedItems([]);
    } else if (action === "reload") {
      refetch();
    }

    setAction("");
  }, [action, navItems, refetch, setAction]);

  return (
    <Grid container gap={1}>
      <Grid size={12}>
        <ModelAutoComplete
          modelName={"Program"}
          label={"Program"}
          value={programOption}
          setValue={setProgram}
          allowCreation
          CreationForm={(props) => <ProgramForm {...props} />}
        />
      </Grid>
      <Grid size={12}>
        <Stack direction={"row"} gap={1} flexWrap="wrap">
          <Chip
            disabled={!program?.uuid}
            icon={<AddCircleOutlineOutlined />}
            label="Add Week"
            clickable
            size="small"
            onClick={() => setAddWeekFormOpen(true)}
          />
          {allExpanded ? (
            <Chip
              disabled={!program?.uuid}
              icon={<UnfoldLess />}
              label="Collapse All"
              clickable
              size="small"
              onClick={() => {
                setAllExpanded(false);
                setAction("collapse-all");
              }}
            />
          ) : (
            <Chip
              disabled={!program?.uuid}
              icon={<UnfoldMore />}
              label="Expand All"
              clickable
              size="small"
              onClick={() => {
                setAllExpanded(true);
                setAction("expand-all");
              }}
            />
          )}
          <Chip
            disabled={!program?.uuid}
            icon={<MoreVert />}
            label="More"
            clickable
            size="small"
            onClick={handleOpenActionMenu}
          />
        </Stack>
      </Grid>
      <Grid size={12}>
        <SimpleTreeView
          expandedItems={expandedItems}
          onExpandedItemsChange={handleExpandedItemsChange}
        >
          <ProgramWeekNavTree
            nested
            program={program}
            navItems={navItems}
            setAction={setAction}
            onProgramDaySelect={onProgramDaySelect}
          />
        </SimpleTreeView>
      </Grid>

      {editProgramFormOpen && (
        <ProgramForm
          open={editProgramFormOpen}
          setOpen={setEditProgramFormOpen}
          setValue={setProgram}
          initialValue={programOption?.label}
          initialUUID={programOption?.uuid}
        />
      )}
      {addWeekFormOpen && (
        <AddWeekToProgramForm
          open={addWeekFormOpen}
          setOpen={setAddWeekFormOpen}
          program={programOption}
          onProgramWeekAdded={onProgramWeekAdded}
        />
      )}
      <ActionMenu />
    </Grid>
  );
}
